// Protected route component for Studio1 Alpha
// Implements route protection based on authentication status

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = "/auth",
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !user) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If authentication is not required but user is authenticated
  // (e.g., auth pages when already logged in)
  if (!requireAuth && user) {
    // Redirect to dashboard or intended location
    const fromLocation = location.state?.from;
    const from = fromLocation?.pathname
      ? fromLocation.pathname +
        (fromLocation.search ?? "") +
        (fromLocation.hash ?? "")
      : "/dashboard";
    return <Navigate to={from} replace />;
  }

  // Render the protected content
  return <>{children}</>;
};
